import { useState, useEffect } from "react";
import api from "../../../utils/api";
import { toast } from "react-toastify";

const emptyForm = { firstName: "", lastName: "", userName: "", role: "waiter" };

const useUserManagement = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", username: "", role: "" });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/users/getusers");
      setUsers(
        (response.data || []).map((u) => ({
          _id: u._id,
          firstName: u.firstName,
          lastName: u.lastName,
          username: u.username,
          role: u.role,
        }))
      );
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "Required";
    if (!form.lastName.trim()) newErrors.lastName = "Required";
    if (!form.userName.trim()) newErrors.userName = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleRoleChange = (role) => setForm((prev) => ({ ...prev, role }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const response = await api.post("/users/addusers", {
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.userName,
        role: form.role,
      });
      toast.success(response.data?.message || "User created successfully!");
      const saved = response.data.user;
      setUsers((prev) => [
        { _id: saved._id, firstName: saved.firstName, lastName: saved.lastName, username: saved.username, role: saved.role },
        ...prev,
      ]);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save user. Try again.");
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({ firstName: user.firstName, lastName: user.lastName, username: user.username, role: user.role });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = { firstName: editForm.firstName, lastName: editForm.lastName, username: editForm.username, role: editForm.role };
      await api.put(`/users/update/${selectedUser._id}`, payload);
      setUsers((prev) => prev.map((u) => (u._id === selectedUser._id ? { ...u, ...payload } : u)));
      toast.success("User updated successfully");
      setEditOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update user");
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/delete/${selectedUser._id}`);
      setUsers((prev) => prev.filter((u) => u._id !== selectedUser._id));
      toast.success("User deleted successfully!");
      setDeleteOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user");
    }
  };

  const handleResetClick = (user) => {
    setSelectedUser(user);
    setResetOpen(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedUser?._id || isResetting) return;
    setIsResetting(true);
    try {
      const res = await api.post(`/users/reset-password/${selectedUser._id}`);
      toast.success(res?.data?.message || "Password reset successfully");
      setResetOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    } finally {
      setIsResetting(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const paginatedUsers = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const getInitials = (firstName, lastName) =>
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();

  return {
    form, errors, loading,
    search, setSearch,
    page, setPage, totalPages, rowsPerPage,
    filteredUsers, paginatedUsers,
    editOpen, setEditOpen, editForm, setEditForm,
    deleteOpen, setDeleteOpen,
    resetOpen, setResetOpen,
    isResetting,
    selectedUser,
    handleFormChange, handleRoleChange, handleSubmit,
    handleEditClick, handleEditSubmit,
    handleDeleteClick, confirmDelete,
    handleResetClick, confirmResetPassword,
    getInitials,
  };
};

export default useUserManagement;
